import React, { useEffect, useRef, useContext, useState } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import logo from '@/assets/images/logo-editada.png';
import gatinho from '@/assets/images/gatinho.png';
import veterinario from '@/assets/images/veterinario.png';
import catshop from '@/assets/images/catshop.png';
import humanizado from '@/assets/images/humanizado.png';
import acolhedor from '@/assets/images/acolhedor.png';
import cuidado from '@/assets/images/cuidado.png';
import veterinaria from '@/assets/images/veterinaria.png';
import whats from '@/assets/images/whats.png';
import insta from '@/assets/images/insta.png';

import InicioPage from '@/pages/Inicio/InicioPage';
import SobrePage from '@/pages/Sobre/SobrePage';
import CatshopPage from '@/pages/Catshop/CatshopPage';
import ClinicaPage from '@/pages/Clinica/ClinicaPage';
import FaqPage from '@/pages/Faq/FaqPage';
import ContatoPage from '@/pages/Contato/ContatoPage';
import { CartIcon } from '@/pages/Catshop/CatshopPage';

import { AppContext } from '@/contexts/AppContext';
import AdminPanel from '@/features/admin/components/AdminPanel';

const App: React.FC = () => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const isCatshopRoute = location.pathname.toLowerCase().includes('catshop');
  const context = useContext(AppContext);
  
  if (!context) {
    throw new Error('App must be used within an AppProvider');
  }
  
  const {
    cart,
    setCart,
    isCartLoaded,
    setIsCartLoaded,
    isCartOpen,
    setIsCartOpen,
    isCheckoutOpen,
    setIsCheckoutOpen,
    orders,
    setOrders,
    customerInfo,
    setCustomerInfo,
    isAdminAuthenticated,
    setIsAdminAuthenticated,
    adminProducts,
    setAdminProducts,
    specialOffers,
    setSpecialOffers,
    coupons,
    appliedCoupon,
    discountCode,
    setDiscountCode,
    selectedCategory,
    setSelectedCategory,
    isCatshopMenuOpen,
    setIsCatshopMenuOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    getCartItemsCount,
    finishOrder,
    applyDiscountCode,
    removeDiscountCode,
    calculateCartTotalWithDiscount,
    getDiscountAmount,
    adminLogin,
    adminLogout,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductStock,
    addSpecialOffer,
    updateSpecialOffer,
    deleteSpecialOffer,
    addCoupon,
    updateCoupon,
    deleteCoupon,
    parsePrice,
    getActiveOffers,
    getProductDiscount,
    calculateDiscountedPrice,
    openWhatsApp
  } = context;

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const [isCepLoading, setIsCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const lastCepLookupRef = useRef<string | null>(null);

  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountFeedback, setDiscountFeedback] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // Categorias originais e ordenação estável para o submenu Catshop
  const uniqueCategories = Array.from(new Set(adminProducts.map((p: any) => p.category))).filter((c): c is string => c !== undefined);
  const categoryOrder = ['Alimentação', 'Brinquedos', 'Higiene', 'Acessórios', 'Saúde', 'Conforto', 'Transporte', 'Donos'];
  const orderedCategories = ['Todos',
    ...categoryOrder.filter(c => uniqueCategories.includes(c)),
    ...uniqueCategories.filter(c => !categoryOrder.includes(c))
  ];

  // Ref para detectar clique fora do submenu do Catshop
  const catshopMenuRef = useRef<HTMLDivElement | null>(null);

  const lookupCep = async (cepInput: string) => {
    const cepDigits = cepInput.replace(/\D/g, '');
    if (cepDigits.length !== 8) return;

    if (lastCepLookupRef.current === cepDigits) {
      return;
    }

    lastCepLookupRef.current = cepDigits;
    setCepError(null);
    setIsCepLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
      if (!response.ok) {
        throw new Error('Erro ao consultar CEP');
      }

      const data: any = await response.json();
      if (data?.erro) {
        setCepError('CEP não encontrado.');
        return;
      }

      setCustomerInfo(prev => ({
        ...prev,
        street: prev.street || data.logradouro || '',
        neighborhood: prev.neighborhood || data.bairro || '',
        complement: prev.complement || data.complemento || '',
        city: prev.city || data.localidade || '',
        state: prev.state || data.uf || '',
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP', error);
      setCepError('Não foi possível buscar o endereço pelo CEP.');
    } finally {
      setIsCepLoading(false);
    }
  };

  // Fechar submenu ao clicar fora ou pressionar Escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isCatshopMenuOpen) return;
      if (catshopMenuRef.current && e.target instanceof Node && !catshopMenuRef.current.contains(e.target)) {
        setIsCatshopMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsCatshopMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCatshopMenuOpen, setIsCatshopMenuOpen]);

  // Fechar submenu em mudança de rota
  useEffect(() => {
    setIsCatshopMenuOpen(false);
  }, [location.pathname, setIsCatshopMenuOpen]);

  // Effect para mudanças de localização (fechar modais e scroll)
  useEffect(() => {
    if (location.pathname !== '/admin') {
      setIsCartOpen(false);
      setIsCheckoutOpen(false);
    }
    setIsMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, setIsCartOpen, setIsCheckoutOpen]);

  const toggleMobileNav = () => {
    setIsMobileNavOpen(prev => !prev);
  };

  const closeMobileNav = () => {
    setIsMobileNavOpen(false);
  };

  // Ajustar overflow global especificamente na página Catshop
  useEffect(() => {
    const html = document.documentElement;

    if (isCatshopRoute) {
      html.classList.add('catshop-page');
      document.body.classList.add('catshop-page');
    } else {
      html.classList.remove('catshop-page');
      document.body.classList.remove('catshop-page');
    }

    return () => {
      html.classList.remove('catshop-page');
      document.body.classList.remove('catshop-page');
    };
  }, [isCatshopRoute]);

  // Verificar URL hash para acesso direto ao admin
  useEffect(() => {
    if (window.location.hash === '#admin') {
      navigate('/admin');
    }
  }, [navigate]);

  return (
    <div className={`App ${isCatshopRoute ? 'App--catshop' : ''}`}>
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src={logo} alt="Logo" className="logo-icon" />
              <h1>A CASA DOS GATOS</h1>
            </div>
            <button
              type="button"
              className={`menu-toggle ${isMobileNavOpen ? 'menu-toggle--open' : ''}`}
              aria-label={isMobileNavOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isMobileNavOpen}
              aria-controls="primary-navigation"
              onClick={toggleMobileNav}
            >
              <span />
              <span />
              <span />
            </button>
            <nav
              id="primary-navigation"
              className={`nav ${isMobileNavOpen ? 'nav--open' : ''}`}
            >
              <NavLink 
                to="/"
                className={({isActive}) => isActive ? 'active' : ''}
                onClick={closeMobileNav}
              >
                Início
              </NavLink>
              <NavLink 
                to="/sobre"
                className={({isActive}) => isActive ? 'active' : ''}
                onClick={closeMobileNav}
              >
                Sobre
              </NavLink>
              <NavLink 
                to="/catshop"
                className={({isActive}) => isActive ? 'active' : ''}
                onClick={closeMobileNav}
              >
                Catshop
              </NavLink>
              <NavLink 
                to="/clinica"
                className={({isActive}) => isActive ? 'active' : ''}
                onClick={closeMobileNav}
              >
                Clínica
              </NavLink>
              <NavLink 
                to="/faq"
                className={({isActive}) => isActive ? 'active' : ''}
                onClick={closeMobileNav}
              >
                FAQ
              </NavLink>
              <NavLink 
                to="/contato"
                className={({isActive}) => isActive ? 'active' : ''}
                onClick={closeMobileNav}
              >
                Contato
              </NavLink>
              <button 
                className="cart-button"
                onClick={() => setIsCartOpen(true)}
                aria-label={`Abrir carrinho com ${getCartItemsCount()} ${getCartItemsCount() === 1 ? 'item' : 'itens'}`}
              >
                <span className="cart-button__icon" aria-hidden="true">
                  <CartIcon />
                </span>
                {getCartItemsCount() > 0 && (
                  <span className="cart-count">{getCartItemsCount()}</span>
                )}
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Cart Sidebar */}
      <main className={`main-content ${isCatshopRoute ? 'main-content--catshop' : ''}`}>
        <Routes>
  <Route path="/" element={<InicioPage />} />
  <Route path="/sobre" element={<SobrePage />} />
  <Route path="/catshop" element={<CatshopPage />} />
  <Route path="/clinica" element={<ClinicaPage />} />
  <Route path="/admin" element={<AdminPanel />} />
  <Route path="/faq" element={<FaqPage />} />
  <Route path="/contato" element={<ContatoPage />} />
  <Route path="*" element={<InicioPage />} />
</Routes>
      </main>

      {/* Modal do Carrinho */}
      {isCartOpen && (
        <div className="modal-overlay" onClick={() => setIsCartOpen(false)}>
          <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cart-header">
              <h2>🛒 Seu Carrinho</h2>
              <button className="close-button" onClick={() => setIsCartOpen(false)}>✕</button>
            </div>
            
            <div className="cart-content">
              {cart.length === 0 ? (
                <p className="empty-cart">Seu carrinho está vazio</p>
              ) : (
                <>
                  <div className="cart-items">
                    {cart.map((item: any) => (
                      <div key={item.id} className="cart-item">
                        <div className="item-info">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="item-emoji"
                              loading="lazy"
                            />
                          ) : (
                            <span className="item-emoji">{item.image}</span>
                          )}
                          <div className="item-details">
                            <h4>{item.name}</h4>
                            <div className="price-with-discount">
                              {item.originalPrice && (
                                <span className="original-price cart-original-price">
                                  R$ {item.originalPrice.toFixed(2).replace('.', ',')}
                                </span>
                              )}
                              <p>{item.price}</p>
                            </div>
                          </div>
                        </div>
                        <div className="item-controls">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                          <span>{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                          <button className="remove-button" onClick={() => removeFromCart(item.id)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="cart-footer">
                    {/* Seção de cupom de desconto (temporariamente desativada)
                    <div className="discount-section">
                      <div className="discount-input-group">
                        <input
                          type="text"
                          placeholder="Código do cupom"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setDiscountFeedback(null);
                            setDiscountError(null);
                          }}
                          className="discount-input"
                        />
                        <button 
                          className="apply-discount-btn"
                          onClick={async () => {
                            if (!discountCode.trim()) return;

                            setDiscountFeedback(null);
                            setDiscountError(null);
                            setIsApplyingDiscount(true);

                            const success = await applyDiscountCode(discountCode.trim());
                            if (success) {
                              setDiscountFeedback('Cupom aplicado com sucesso!');
                            } else {
                              setDiscountError('Cupom inválido, expirado ou que não atende aos requisitos mínimos.');
                            }

                            setIsApplyingDiscount(false);
                          }}
                          disabled={!discountCode.trim() || isApplyingDiscount}
                        >
                          {isApplyingDiscount ? 'Aplicando...' : 'Aplicar'}
                        </button>
                      </div>

                      {discountFeedback && <p className="success">{discountFeedback}</p>}
                      {discountError && <p className="error">{discountError}</p>}

                      {appliedCoupon && (
                        <div className="applied-discount">
                          <div className="discount-info">
                            <span className="discount-code-applied">💰 {appliedCoupon.code}</span>
                            <span className="discount-value">
                              {appliedCoupon.calculatedAmountCents != null
                                ? `- ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                                    .format(appliedCoupon.calculatedAmountCents / 100)}`
                                : `-${appliedCoupon.percentage}%`}
                            </span>
                            <button 
                              className="remove-discount-btn"
                              onClick={() => {
                                removeDiscountCode();
                                setDiscountFeedback(null);
                                setDiscountError(null);
                              }}
                              title="Remover cupom"
                            >
                              ✕
                            </button>
                          </div>
                          {appliedCoupon.description && (
                            <div className="discount-description">{appliedCoupon.description}</div>
                          )}
                        </div>
                      )}
                    </div>
                    */}
                    
                    <div className="cart-total">
                      {appliedCoupon ? (
                        <>
                          <div className="subtotal">Subtotal: {getCartTotal().toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                          <div className="discount-amount">Desconto: -{getDiscountAmount(getCartTotal()).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                          <div className="final-total"><strong>Total: {calculateCartTotalWithDiscount(getCartTotal()).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></div>
                        </>
                      ) : (
                        <strong>Total: {getCartTotal().toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                      )}
                    </div>
                    
                    <div className="cart-actions">
                      <button className="continue-shopping" onClick={() => setIsCartOpen(false)}>
                        Continuar Comprando
                      </button>
                      <button className="checkout-button" onClick={() => { setIsCartOpen(false); setIsCheckoutOpen(true); }}>
                        Finalizar Pedido
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal do Checkout */}
      {isCheckoutOpen && (
        <div className="modal-overlay" onClick={() => setIsCheckoutOpen(false)}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-header">
              <h2>📋 Finalizar Pedido</h2>
              <button className="close-button" onClick={() => setIsCheckoutOpen(false)}>✕</button>
            </div>
            
            <div className="checkout-content">
              <div className="customer-form">
                <h3>Dados do Cliente</h3>
                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Telefone *</label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>CEP *</label>
                  <input
                    type="text"
                    value={customerInfo.zipCode}
                    onChange={(e) => {
                      setCepError(null);
                      setCustomerInfo({...customerInfo, zipCode: e.target.value});
                    }}
                    onBlur={(e) => lookupCep(e.target.value)}
                    required
                  />
                  {isCepLoading && <p>Buscando endereço...</p>}
                  {cepError && <p className="error">{cepError}</p>}
                </div>
                <div className="form-group">
                  <label>Rua/Avenida *</label>
                  <input
                    type="text"
                    value={customerInfo.street}
                    onChange={(e) => setCustomerInfo({...customerInfo, street: e.target.value})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Número *</label>
                    <input
                      type="text"
                      value={customerInfo.number}
                      onChange={(e) => setCustomerInfo({...customerInfo, number: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Bairro *</label>
                    <input
                      type="text"
                      value={customerInfo.neighborhood}
                      onChange={(e) => setCustomerInfo({...customerInfo, neighborhood: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Complemento</label>
                  <input
                    type="text"
                    value={customerInfo.complement || ''}
                    onChange={(e) => setCustomerInfo({...customerInfo, complement: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cidade *</label>
                    <input
                      type="text"
                      value={customerInfo.city}
                      onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Estado</label>
                    <input
                      type="text"
                      value={customerInfo.state || ''}
                      onChange={(e) => setCustomerInfo({...customerInfo, state: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="order-summary">
                <h3>Resumo do Pedido</h3>
                <div className="summary-items">
                  {cart.map((item: any) => (
                    <div key={item.id} className="summary-item">
                      <span>{item.name} x{item.quantity}</span>
                      <span>{(parsePrice(item.price) * item.quantity).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                    </div>
                  ))}
                </div>
                <div className="summary-total">
                  <strong>Total: {getCartTotal().toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                </div>
              </div>
              
              <div className="checkout-actions">
                <button 
                  className="finish-order-button"
                  onClick={finishOrder}
                  disabled={!customerInfo.name || !customerInfo.phone || !customerInfo.street || !customerInfo.number || !customerInfo.neighborhood || !customerInfo.city || !customerInfo.zipCode}
                >
                  Enviar Pedido via WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rodapé */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo logo">
              <img src={logo} alt="Logo" className="logo-icon" />
              <h1>A CASA DOS GATOS</h1>
            </div>
            
            <div className="footer-social">
              <button onClick={() => {
                openWhatsApp('Olá! Gostaria de mais informações sobre os serviços da Casa dos Gatos.');
              }}>
                <img src={whats} alt="WhatsApp" />
              </button>
              <button onClick={() => window.open('https://instagram.com/acasadosgatos', '_blank')}>
                <img src={insta} alt="Instagram" />
              </button>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 A Casa dos Gatos. Todos os direitos reservados.</p>
            <p>Clínica veterinária exclusiva para felinos - Lages/SC</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
